import {Component,EventEmitter,Input, NgZone,Output,ViewChild} from '@angular/core';
import {CellClickEvent, GridComponent, GridDataResult, PageChangeEvent, RowClassArgs, SelectableSettings, SelectionEvent, SortSettings} from '@progress/kendo-angular-grid';
import {orderBy, SortDescriptor} from '@progress/kendo-data-query';
import {ColumnDefinition} from './column-definition';
import {take} from "rxjs/internal/operators";
import {ExcelExportData} from "@progress/kendo-angular-excel-export";

@Component({
    selector: 'fw-dynamic-grid',
    template: require('./dynamic-grid.component.html'),
    styles: [require('./dynamic-grid.component.css')]
})
export class DynamicGridComponent {
    @Input() vmColumnDefinition: Array<ColumnDefinition>;
    @Input() rowKey: string;
    @Input() excelFilename: string;
    @Input() pdfFilename: string;
    @Input() selectMode: 'single' | 'multiple' = 'single';
    @Output() gridRowDoubleClick = new EventEmitter<any>();
    @Output() selectionChanged = new EventEmitter<any>();
    @Output() rowDataValueChanged = new EventEmitter<any>();
    @Output() cellClicked = new EventEmitter<any>();
    @ViewChild('grid', {static: false}) grid: GridComponent;
    resizable: boolean = false;
    hasImages: boolean = false;
    hasSmallImages: boolean = false;
    selectedIds: any[] = [];
    hiddenColumns: string[] = [];
    selectableSettings: SelectableSettings = {
        checkboxOnly: false,
        enabled: true,
        mode: 'single'
    };
    sortableSettings: SortSettings = {
        mode: 'single'
    };
    gridSort: SortDescriptor[] = [];
    gridView: GridDataResult;
    pageSize = 100;
    skip = 0;
    private selectedIndex: number;
    private selectedItem: any;
    private rowHeight: number = 35;
    private data: any[];
    private sortedData: any[];

    constructor(private ngZone: NgZone) {
        this.allData = this.allData.bind(this);
    }

    ngOnInit() {
        this.selectableSettings = {
            checkboxOnly: false,
            mode: this.selectMode
        };
    }

    gridSortChange(sort: SortDescriptor[]): void {
        this.gridSort = sort;
        this.showData(this.data);
    }

    pageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.showPagedData();
    }

    gridCellClick(event: CellClickEvent) {
        this.cellClicked.emit(event);
    }

    showData(data: any[]) {
        this.data = data;
        this.sortedData = orderBy(this.data, this.gridSort);
        this.selectedIds = [];
        this.selectedItem = null;

        if (this.data && this.data.length > 0)
            this.showPagedData();
        else
            this.clearData();
    }

    showPagedData() {
        let dataToShow = this.sortedData.slice(this.skip, this.skip + this.pageSize);
        this.gridView = {
            data: dataToShow,
            total: this.sortedData.length
        };
    }

    refreshView() {
        if (this.data && this.data.length > 0) this.showPagedData();
    }

    clearData() {
        this.data = [];
        this.selectedIds = [];
        this.skip = 0;

        this.gridView = {
            data: [],
            total: 0
        };
    }

    getData() {
        if (this.gridView) return this.gridView.data;
        else return null;
    }

    getAllData() {
        return this.data;
    }

    getDatasetSize() {
        if (this.data) {
            return this.data.length;
        }

        return 0;
    }

    getSelectedId(): any {
        if (this.selectedIds.length == 1) {
            return this.selectedIds[0];
        }
        return 0;
    }

    getSelectedItem(): any {
        return this.selectedItem;
    }

    getMultipleSelectedIds(): any[] {
        return this.selectedIds
    }

    selectFirstRow(id: any) {
        if (this.getData()) {
            this.selectedIndex = this.getData().indexOf(this.getData()[0]);
            this.selectedIds = [id];
        }
    }

    rowdoubleClicked() {
        this.gridRowDoubleClick.emit(this.getSelectedId());
    }

    setSelectedId(id: any) {
        this.selectedIds = [id];
    }

    gridSelectionChange(event: SelectionEvent) {
        if (event.selectedRows.length > 0) {
            this.selectedItem = event.selectedRows[0].dataItem;
            this.selectionChanged.emit(this.getSelectedItem());
        } else {
            this.selectedItem = null;
        }
    }

    showColumn(type: string) {
        if (type != 'image') {
            return true;
        } else if (this.hasImages || this.hasSmallImages) {
            return true;
        }

        return false;
    }

    getColumnStyle(type: string, isEnabled: boolean) {
        if (type == 'image') {
            return 'grid-image-column';
        } else if (type == 'color') {
            return 'no-padding';
        } else if (this.hasImages) {
            return 'grid-row-image';
        } else if (this.hasSmallImages) {
            return 'grid-row-image-small';
        }else if (type == 'currency') {
            return 'currency';
        } else if (type == 'number' && isEnabled) {
            return 'no-padding';
        }

        return '';
    }

    rowCallback = (context: RowClassArgs) => {
        switch (context.dataItem.rowColor) {
            case '#d3d3d3':
                return {rowColor1: true};
        }

        switch (context.dataItem.style) {
            case 'textbold':
                return {textbold: true};
        }

        return {};
    };

    scrollToIndex(index: number) {
        this.grid.wrapper.nativeElement.querySelector('.k-grid-content').scrollTop = this.rowHeight * index;
    }

    public allData(): ExcelExportData {
        const result: ExcelExportData =  {
            data: orderBy(this.data, this.gridSort)
        };

        return result;
    }

    saveAsExcel() {
        if (this.excelFilename) this.grid.saveAsExcel();
    }

    saveAsPdf() {
        if (this.pdfFilename) this.grid.saveAsPDF();
    }

    setHiddenColumns(columns: string[]) {
        setTimeout(() => {
            this.hiddenColumns = columns;
        });
    }

    fitColumns(): void {
        this.ngZone.onStable.asObservable().pipe(take(1)).subscribe(() => {
            this.grid.autoFitColumns();
        });
    }

    setResizable(resizable: boolean) {
        this.resizable = resizable;
    }

    checkBoxClicked(dataItem, event) {
        const checkbox = event.target as HTMLInputElement;
        this.rowDataValueChanged.emit({dataItem: dataItem, value: checkbox.checked});
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/fw/dynamic-grids/dynamic-grid.component.ts